import api from '../utils/api';

/**
 * Frontend SDK for the IDEXX VetConnect+ integration. Mirrors keystoneService
 * for shape and conventions. See backend/src/modules/idexx/README.md for the
 * underlying design (the full spec is Docs/idexx/, only in commit
 * c61670f4 — see that README's note).
 *
 * W1 ships only the config endpoints — the lab-request lifecycle, ref-data
 * picker, viewer deep-link, and PDF stream methods land in later sprints.
 */

export type IdexxRegion = 'US' | 'CA_EAST' | 'CA_WEST';

export interface IdexxRegionDescriptor {
  region: IdexxRegion;
  label: string;
  currencyCode: string;
  countryCode: string;
  enabled: boolean;
}

export interface IdexxConfigPayload {
  /**
   * Omit on reconfigure — the backend resolves it (stored config wins, and a
   * conflicting region is a 409: VC+ credentials are linked to a region).
   * On first connect it's required only when several regions are enabled.
   */
  region?: IdexxRegion;
  apiUsername: string;
  apiPassword: string;
  limsId?: string;
  enabled?: boolean;
}

export type IdexxConfig =
  | { configured: false }
  | {
      configured: true;
      enabled: boolean;
      region: IdexxRegion;
      apiUsername: string;
      limsId?: string | null;
      pollIntervalSec: number;
      lastConnectedAt?: string | null;
      lastPolledAt?: string | null;
      lastError?: string | null;
      lastErrorAt?: string | null;
      refDataRefreshedAt?: string | null;
    };

export interface IdexxRefTest {
  region: IdexxRegion;
  code: string;
  name: string;
  displayCode?: string | null;
  listPrice?: string | null;
  currencyCode?: string | null;
  turnaround?: string | null;
  specimen?: string | null;
  addOn: boolean;
  allowsAddOns: boolean;
  equineApplicable: boolean;
  equineFavorite: boolean;
}

export interface IdexxRefBreed {
  region: IdexxRegion;
  code: string;
  name: string;
  speciesCode: string;
}

export interface IdexxRefGender {
  region: IdexxRegion;
  code: string;
  name: string;
}

export interface IdexxRefSyncSummary {
  skipped: boolean;
  reason?: string;
  versions?: { species: string; breeds: string; genders: string; tests: string };
  counts: { species: number; breeds: number; genders: number; tests: number; ivlsDevices: number };
}

export interface IdexxIvlsDevice {
  region: IdexxRegion;
  clinicId: string;
  serialNumber: string;
  displayName: string;
  vcpStatus: string;
  lastPolledAt?: string | null;
}

export type LabRequestStatus =
  | 'DRAFT'
  | 'CREATED'
  | 'SUBMITTED'
  | 'AT_LAB'
  | 'PARTIAL'
  | 'COMPLETE'
  | 'CANCELLED'
  | 'FAILED';

export interface LabRequest {
  id: string;
  clinicId: string;
  horseId: string;
  provider: 'IDEXX' | string;
  status: LabRequestStatus;
  idexxOrderId?: string | null;
  idexxStatus?: string | null;
  idexxStatusDetail?: string | null;
  uiUrl?: string | null;
  pdfUrl?: string | null;
  lastSyncedAt?: string | null;
  lastError?: string | null;
  veterinarian: string;
  technician?: string | null;
  specimenCollectionDate?: string | null;
  notes?: string | null;
  prevRefNum?: string | null;
  modality?: 'REFLAB' | 'INHOUSE' | string | null;
  testCodes: string[];
  ivlsSerialNumber?: string | null;
  submittedAt?: string | null;
  submittedBy?: string | null;
  cancelledAt?: string | null;
  cancelledBy?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLabRequestPayload {
  horseId: string;
  modality: 'REFLAB' | 'INHOUSE';
  veterinarian: string;
  technician?: string;
  specimenCollectionDate?: string;
  notes?: string;
  prevRefNum?: string;
  testCodes?: string[];
  ivlsSerialNumber?: string;
  soapNoteId?: string;
}

export type UpdateLabRequestPayload = Partial<
  Omit<CreateLabRequestPayload, 'horseId' | 'modality' | 'soapNoteId'>
>;

export interface IdexxConfigTestResult {
  success: boolean;
  message: string;
  error?: string;
}

export interface IdexxLabPanel {
  id: string;
  clinicId: string | null;
  name: string;
  description?: string | null;
  modality: 'INHOUSE' | 'REFLAB';
  testCodes: string[];
  isSystem: boolean;
  sortOrder: number;
  tests: { code: string; name: string }[];
}

export interface PanelPayload {
  name: string;
  description?: string;
  modality: 'INHOUSE' | 'REFLAB';
  testCodes?: string[];
  sortOrder?: number;
}

class IdexxService {
  async listRegions(): Promise<IdexxRegionDescriptor[]> {
    const response = await api.get('/idexx/regions');
    return response.data;
  }

  async getConfig(): Promise<IdexxConfig> {
    const response = await api.get('/idexx/config');
    return response.data;
  }

  async testConfig(payload: IdexxConfigPayload): Promise<IdexxConfigTestResult> {
    const response = await api.post('/idexx/config/test', payload);
    return response.data;
  }

  async saveConfig(payload: IdexxConfigPayload): Promise<IdexxConfig> {
    const response = await api.post('/idexx/config', payload);
    return response.data;
  }

  async deleteConfig(): Promise<{ ok: true }> {
    const response = await api.delete('/idexx/config');
    return response.data;
  }

  // ─── Reference data ─────────────────────────────────────────────────────

  async listTests(opts: { equineOnly?: boolean; includeAddOns?: boolean } = {}): Promise<IdexxRefTest[]> {
    const params = new URLSearchParams();
    if (opts.equineOnly === false) params.set('equineOnly', 'false');
    if (opts.includeAddOns) params.set('includeAddOns', 'true');
    const qs = params.toString();
    const response = await api.get(`/idexx/ref/tests${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async listBreeds(): Promise<IdexxRefBreed[]> {
    const response = await api.get('/idexx/ref/breeds');
    return response.data;
  }

  async listGenders(): Promise<IdexxRefGender[]> {
    const response = await api.get('/idexx/ref/genders');
    return response.data;
  }

  async refreshRefData(force = false): Promise<IdexxRefSyncSummary> {
    const response = await api.post('/idexx/refdata/refresh', { force });
    return response.data;
  }

  async listIvlsDevices(): Promise<IdexxIvlsDevice[]> {
    const response = await api.get('/idexx/ivls/devices');
    return response.data;
  }

  // ─── Lab requests ───────────────────────────────────────────────────────

  async listLabRequests(horseId?: string): Promise<LabRequest[]> {
    const qs = horseId ? `?horseId=${encodeURIComponent(horseId)}` : '';
    const response = await api.get(`/idexx/lab-requests${qs}`);
    return response.data;
  }

  async getLabRequest(id: string): Promise<LabRequest> {
    const response = await api.get(`/idexx/lab-requests/${id}`);
    return response.data;
  }

  async createLabRequest(payload: CreateLabRequestPayload): Promise<LabRequest> {
    const response = await api.post('/idexx/lab-requests', payload);
    return response.data;
  }

  async updateLabRequest(id: string, payload: UpdateLabRequestPayload): Promise<LabRequest> {
    const response = await api.put(`/idexx/lab-requests/${id}`, payload);
    return response.data;
  }

  async submitLabRequest(id: string): Promise<LabRequest> {
    const response = await api.post(`/idexx/lab-requests/${id}/submit`);
    return response.data;
  }

  async refreshLabRequest(id: string): Promise<LabRequest> {
    const response = await api.post(`/idexx/lab-requests/${id}/refresh`);
    return response.data;
  }

  async cancelLabRequest(id: string, reason?: string): Promise<LabRequest> {
    const response = await api.post(`/idexx/lab-requests/${id}/cancel`, { reason });
    return response.data;
  }

  async deleteLabRequest(id: string): Promise<{ ok: true }> {
    const response = await api.delete(`/idexx/lab-requests/${id}`);
    return response.data;
  }

  // ─── Result viewer + PDFs (launchpad into VC+) ──────────────────────────

  /** Single-use VC+ Results Viewer URL for a result. Open immediately; don't cache. */
  async getResultViewUrl(resultId: string): Promise<{ url: string }> {
    const response = await api.get(
      `/idexx/results/${encodeURIComponent(resultId)}/view-url`,
    );
    return response.data;
  }

  /** Fetch the result report PDF (proxied, so auth stays server-side) and open it. */
  async openResultPdf(resultId: string): Promise<void> {
    const response = await api.get(
      `/idexx/results/${encodeURIComponent(resultId)}/pdf`,
      { responseType: 'blob' },
    );
    const blobUrl = URL.createObjectURL(response.data as Blob);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  }

  /** Reference-lab requisition-form PDF URL (signed; null until the order is finalized). */
  async getReqFormUrl(labRequestId: string): Promise<{ url: string | null }> {
    const response = await api.get(
      `/idexx/lab-requests/${labRequestId}/req-form-url`,
    );
    return response.data;
  }

  // ─── Lab panels (reusable test bundles) ─────────────────────────────────

  async listPanels(modality?: 'INHOUSE' | 'REFLAB'): Promise<IdexxLabPanel[]> {
    const qs = modality ? `?modality=${modality}` : '';
    const response = await api.get(`/idexx/panels${qs}`);
    return response.data;
  }

  async createPanel(payload: PanelPayload): Promise<IdexxLabPanel> {
    const response = await api.post('/idexx/panels', payload);
    return response.data;
  }

  async updatePanel(id: string, payload: Partial<PanelPayload>): Promise<IdexxLabPanel> {
    const response = await api.put(`/idexx/panels/${id}`, payload);
    return response.data;
  }

  async deletePanel(id: string): Promise<{ ok: true }> {
    const response = await api.delete(`/idexx/panels/${id}`);
    return response.data;
  }
}

export const idexxService = new IdexxService();
